import React, { useState, useRef, useEffect, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { Stack, Box, Table, TableRow, TableBody, TableCell, Typography, SxProps, Button } from '@mui/material';

import { OpenEndedAssistantController } from 'controllers/openEndedAssistant';
import { useFeatures } from 'components/FeaturesContext';
import { TranscriptionControllerFactory } from 'controllers/transcription/transcriptionControllerFactory';
import { TranscriptionController, TranscriptionMessage } from 'controllers/transcription/types';
import { AnimatedThreeDots } from 'components/common/AnimatedThreeDots';
import { UserContext } from 'components/UserContext';
import { OpenEndedMicButton } from './OpenEndedMicButton';
import { OpenEndedTextInput } from './OpenEndedTextInput';
import { OpenEndedChatAvatar } from './OpenEndedChatAvatar';
import { OpenEndedFeedback } from '../OpenEndedFeedback/OpenEndedFeedback';

const styles: Record<string, SxProps> = {
  aiMessage: {
    width: 'fit-content',
    fontSize: '0.75em',
  },
  aiMessageContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  openEndedActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  openEndedBox: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'scroll',
    height: '8em', // have to have this to make the scroll to work. TODO: We should dynamically calculate
  },
  openEndedTable: {
    py: '1',
    px: '3',
    width: 'fit-content',
  },
};

type ConversationItem = {
  role: string;
  text: string;
};

const ROLE_USER = 'You';
const ROLE_AI = 'Kyron';

export const OpenEnded = ({
  chatId,
  handleOpenEnded,
  question,
}: {
  chatId: number;
  handleOpenEnded: (status: string) => void;
  question: string | null;
}) => {
  // TODO: remove the Speechmatics option and only use Whisper
  const { open_ended_question_should_use_whisper: shouldUseWhisper = false } = useFeatures();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);
  const scrollRef = useRef<HTMLSpanElement>(null);
  const [chatStatus, setChatStatus] = useState('');
  const [isAssistantResponding, setIsAssistantResponding] = useState(false);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);

  const transcriptionControllerRef = useRef<TranscriptionController | null>(null);

  const [finalizedTranscriptPortion, setFinalizedTranscriptPortion] = useState('');
  const [partialTranscriptPortion, setPartialTranscriptPortion] = useState('');

  const lastTranscriptPortionStartRef = useRef(0);

  // Must be a ref because it is used by RealtimeTranscriptionController event listeners independent of renders
  const hasReceivedNewTranscriptSinceStudentMessageRef = useRef(true);

  const [micButtonMuted, setMicButtonMuted] = useState(true);
  const [isTranscriptInitializationInProgress, setIsTranscriptInitializationInProgress] = useState(true);

  const [isUserTyping, setIsUserTyping] = useState(false);
  const [userTypingAnimationPace, setUserTypingAnimationPace] = useState('slow');

  const addConversationMessage = (text: string, role = ROLE_AI) => {
    setConversation(prevConvo => [...prevConvo, { role, text }]);
  };

  const appendToLastMessage = (text: string) => {
    setConversation(prevConvo => {
      const lastMessage = prevConvo[prevConvo.length - 1];
      lastMessage.text += text;
      return [...prevConvo];
    });
  };

  const replaceLastMessage = (text: string) => {
    setConversation(prevConvo => {
      const lastMessage = prevConvo[prevConvo.length - 1];
      lastMessage.text = text;
      return [...prevConvo];
    });
  };

  const assistantControllerRef = useRef<OpenEndedAssistantController | null>(null);

  // Both playing received synthesized voice audio chunks,
  // and recording the user's voice + sending it to Speechmatics + handling the received transcription
  const stopAllVoiceMedia = () => {
    // Stop playing the synthesized voice audio chunks
    if (assistantControllerRef.current) assistantControllerRef.current.stop();

    // Stop sending the recorded audio and close session
    if (!transcriptionControllerRef.current) return;
    transcriptionControllerRef.current.pause();
    transcriptionControllerRef.current.stop();
  };

  useEffect(() => {
    assistantControllerRef.current = new OpenEndedAssistantController(
      chatId,
      appendToLastMessage,
      setIsAssistantResponding,
      enqueueSnackbar,
      setChatStatus,
    );
    return stopAllVoiceMedia; // On unmount, cleanup streams
  }, [chatId]); // eslint-disable-line

  // Reset the conversation when the chatId changes
  useEffect(() => {
    setConversation([]);
    setChatStatus('');
    setIsAssistantResponding(false);
    setMicButtonMuted(true);
    setIsTranscriptInitializationInProgress(true);
    setPartialTranscriptPortion('');
    setFinalizedTranscriptPortion('');
    lastTranscriptPortionStartRef.current = 0;
    hasReceivedNewTranscriptSinceStudentMessageRef.current = true;
  }, [chatId]);

  // This function takes an input string as the current conversation turn.
  const submitResponse = (chat: string) => {
    if (isAssistantResponding) return;
    if (!chat) return;

    setConversation(prevConvo => [...prevConvo, { role: user?.first_name || ROLE_USER, text: chat }]);
    setIsAssistantResponding(true);
    hasReceivedNewTranscriptSinceStudentMessageRef.current = false;

    // Add an empty message to the conversation from the assistant,
    // to be filled in by the assistant's streaming response.
    addConversationMessage('');
    if (assistantControllerRef.current) {
      assistantControllerRef.current.stop(); // If the user is trying to interrupt the assistant, stop the current audio playback
      assistantControllerRef.current.streamResponse(chat);
    }
  };

  // When the user submits their question, we want to stop transcribing audio
  // When the AI finishes replying, we want to start transcribing again
  useEffect(() => {
    if (transcriptionControllerRef.current)
      transcriptionControllerRef.current.pause(isAssistantResponding || micButtonMuted);
    setPartialTranscriptPortion('');
    setFinalizedTranscriptPortion('');
  }, [isAssistantResponding, micButtonMuted]);

  const shouldIgnoreTranscript = (message: TranscriptionMessage) => {
    const start = message.metadata.start_time;
    if (!hasReceivedNewTranscriptSinceStudentMessageRef.current && start === lastTranscriptPortionStartRef.current) {
      console.warn(`Received partial transcript from before student message - ignoring:${message.metadata.transcript}`);
      // This audio was sent to Speechmatics before the student sent their message, so we can ignore it
      console.debug('ignoring');
      return true; // Should return from parent context
    }
    console.debug('not ignoring');
    return false; // Should continue with parent context
  };

  const handlePartialTranscript = (message: TranscriptionMessage) => {
    console.debug('handlePartial: ', message);
    if (shouldIgnoreTranscript(message)) return;

    const partialTranscript = message.metadata.transcript;
    setPartialTranscriptPortion(partialTranscript);
  };

  const handleFinalTranscript = (message: TranscriptionMessage) => {
    console.debug('handleFinal: ', message);
    if (shouldIgnoreTranscript(message)) {
      hasReceivedNewTranscriptSinceStudentMessageRef.current = true;
      lastTranscriptPortionStartRef.current = message.metadata.start_time;
      return;
    }

    // When we receive a finalized transcript, we need to shift it to the left into the finalized portion of the state,
    // and clear the partial portion.
    const finalizedSegment = message.metadata.transcript;
    setFinalizedTranscriptPortion(prevTranscript => prevTranscript + finalizedSegment);
    setPartialTranscriptPortion('');
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  const weAreDone = () =>
    chatStatus === 'done_finished' || chatStatus === 'done_max_turns' || chatStatus === 'done_moderation';

  const handleMicButtonClick = (muted: boolean) => {
    console.debug('handeMicButtonClick: ', muted);
    setMicButtonMuted(muted);
    if (!transcriptionControllerRef.current) {
      // If we're clicking for the first time, we need to initialize the controller
      transcriptionControllerRef.current = TranscriptionControllerFactory.create(
        shouldUseWhisper,
        handlePartialTranscript,
        handleFinalTranscript,
      );
      setIsTranscriptInitializationInProgress(false);
      transcriptionControllerRef.current.initiate(() => setIsTranscriptInitializationInProgress(true));
    }

    setIsUserTyping(true);
    setUserTypingAnimationPace('slow');
    if (muted) {
      setUserTypingAnimationPace('fast'); // Slow when recording, fast when waiting for whisper response
      transcriptionControllerRef.current
        .awaitTranscription()
        .then((transcription: string | void) => {
          if (transcription) {
            // this is where we submit the text if using Whisper
            console.debug('trans: ', transcription);
            submitResponse(transcription);
          } else {
            // this is where we submit the text if using Speechmatics
            console.debug('not trans: ', finalizedTranscriptPortion, '//', partialTranscriptPortion);
            submitResponse(`${finalizedTranscriptPortion}${partialTranscriptPortion}`);
            setPartialTranscriptPortion('');
            setFinalizedTranscriptPortion('');
          }
          setIsUserTyping(false);
        })
        .catch((err: Error) => {
          console.error(err);
          setIsUserTyping(false);
        });
    } else {
      // They clicked the button to start recording
      // Reset the recorder so we don't include any audio from before they clicked unmute
      console.debug('resetting transcription');
      transcriptionControllerRef.current.reset?.();
      setPartialTranscriptPortion('');
      setFinalizedTranscriptPortion('');
    }
  };

  return (
    <Stack display='flex' justifyContent='center'>
      <Box sx={styles.openEndedBox}>
        <Table sx={styles.openEndedTable}>
          <TableBody>
            {conversation.map((turn: ConversationItem, idx: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={idx}>
                <TableCell sx={{ padding: '0.4em', width: '90%' }}>
                  <Stack direction='row' spacing={2}>
                    <OpenEndedChatAvatar role={turn.role} />
                    {idx === conversation.length - 1 && turn.role === ROLE_AI && turn.text === '' ? (
                      <AnimatedThreeDots size={15} />
                    ) : (
                      <Stack direction='column' alignItems='flex-start'>
                        <Typography component='div'>{turn.text}</Typography>
                        {idx === conversation.length - 1 && turn.role === ROLE_AI && (
                          <OpenEndedFeedback
                            aiText={turn.text}
                            chatId={chatId}
                            rejectedMessage={turn.text}
                            replaceLastMessage={replaceLastMessage}
                            conversation={conversation}
                            question={question}
                          />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {isUserTyping && (
              <TableRow>
                <TableCell sx={{ padding: '0.4em' }}>
                  {ROLE_USER}: <AnimatedThreeDots pace={userTypingAnimationPace} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <span ref={scrollRef} />
      </Box>
      <Box sx={styles.openEndedActions}>
        {!weAreDone() && (
          <>
            <OpenEndedMicButton
              isTranscriptInitializationInProgress={isTranscriptInitializationInProgress}
              micButtonMuted={micButtonMuted}
              setMicButtonMuted={handleMicButtonClick}
            />
            <OpenEndedTextInput micButtonMuted={micButtonMuted} handleSend={submitResponse} />
          </>
        )}
        {weAreDone() && (
          <Button
            sx={{
              borderRadius: '100px',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
                color: 'primary.contrastText',
              },
            }}
            onClick={() => handleOpenEnded(chatStatus)}
          >
            Continue
          </Button>
        )}
      </Box>
      <Box sx={styles.aiMessageContent}>
        <Typography sx={styles.aiMessage}>All responses above are generated by AI.</Typography>
      </Box>
    </Stack>
  );
};
